"use client";

import {
  CalendarCheck,
  CheckCheck,
  Filter,
  type LucideIcon,
  Mail,
  MessageSquare,
  MousePointerClick,
  Reply,
  Users,
} from "lucide-react";
import type { ReactNode } from "react";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { SectionHeading } from "../../home/section";
import { Stagger, StaggerItem } from "../../home/motion";
import { CountUp } from "./count-up";

/* ------------------------------------------------------------------ */
/* Shared mock chrome                                                 */
/* ------------------------------------------------------------------ */

function MiniCard({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div className={cn("rounded-xl border border-border bg-background shadow-sm", className)}>
      {children}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* 1 · Targeting — segment precision or the whole list                */
/* ------------------------------------------------------------------ */

function FilterRow({ children }: { children: ReactNode }) {
  return (
    <div className="flex items-center gap-2 rounded-lg border border-border bg-muted/40 px-2.5 py-1.5">
      <Filter className="size-3 shrink-0 text-muted-foreground" />
      <span className="min-w-0 truncate text-[12px] font-medium text-foreground">{children}</span>
    </div>
  );
}

function SegmentMock() {
  return (
    <MiniCard className="w-full max-w-[244px] overflow-hidden">
      <div className="flex items-center justify-between border-b border-border p-3">
        <span className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-foreground">
          <Users className="size-3.5 text-muted-foreground" />
          Audience
        </span>
        <Badge variant="outline" className="shrink-0 bg-background text-[10px] font-semibold">
          1,240 people
        </Badge>
      </div>
      <div className="space-y-2 p-3">
        <FilterRow>Membership is lapsed</FilterRow>
        <FilterRow>Last visit over 30 days</FilterRow>
        <span className="block px-0.5 text-[11px] text-muted-foreground">+ Add filter</span>
      </div>
    </MiniCard>
  );
}

/* ------------------------------------------------------------------ */
/* 2 · Channel — priority text, email fallback, or either alone       */
/* ------------------------------------------------------------------ */

function ChannelRow({
  icon: Icon,
  label,
  badge,
  active,
}: {
  icon: LucideIcon;
  label: string;
  badge: string;
  active?: boolean;
}) {
  return (
    <div
      className={cn(
        "flex items-center gap-2.5 rounded-lg border px-3 py-2",
        active
          ? "border-primary/40 bg-primary/5 dark:border-brand-accent/40 dark:bg-brand-accent/5"
          : "border-border bg-background",
      )}
    >
      <Icon
        className={cn(
          "size-4 shrink-0",
          active ? "text-primary dark:text-brand-accent" : "text-muted-foreground",
        )}
      />
      <span className="text-[13px] font-semibold text-foreground">{label}</span>
      <Badge variant="outline" className="ml-auto shrink-0 bg-background text-[10px]">
        {badge}
      </Badge>
    </div>
  );
}

function ChannelMock() {
  return (
    <MiniCard className="w-full max-w-[244px] overflow-hidden">
      <div className="border-b border-border px-3 py-2.5 text-[12px] font-semibold text-foreground">
        Delivery
      </div>
      <div className="space-y-2 p-3">
        <ChannelRow icon={MessageSquare} label="Text" badge="Priority" active />
        <ChannelRow icon={Mail} label="Email" badge="Fallback" />
      </div>
    </MiniCard>
  );
}

/* ------------------------------------------------------------------ */
/* 3 · Real-time results — track every send as it lands               */
/* ------------------------------------------------------------------ */

function MetricCell({
  icon: Icon,
  label,
  to,
  suffix,
}: {
  icon: LucideIcon;
  label: string;
  to: number;
  suffix?: string;
}) {
  return (
    <div className="flex flex-col gap-0.5 px-3 py-2.5">
      <span className="inline-flex items-center gap-1 text-[10px] font-medium text-muted-foreground">
        <Icon className="size-2.5 shrink-0" />
        {label}
      </span>
      <span className="text-[15px] font-bold tabular-nums tracking-tight text-foreground">
        <CountUp to={to} />
        {suffix ? (
          <span className="text-[12px] font-medium text-muted-foreground">{suffix}</span>
        ) : null}
      </span>
    </div>
  );
}

function MetricsStripMock() {
  return (
    <MiniCard className="w-full max-w-[244px] overflow-hidden">
      <div className="flex items-center justify-between border-b border-border p-3">
        <span className="truncate text-[13px] font-semibold text-foreground">Win-back promo</span>
        <span className="inline-flex shrink-0 items-center gap-1.5 rounded-full bg-success/10 px-2 py-0.5 text-[10px] font-medium text-success">
          <span className="size-1.5 rounded-full bg-success" />
          Live
        </span>
      </div>
      <div className="grid grid-cols-2 gap-px border-t border-border bg-border [&>*]:bg-background">
        <MetricCell icon={CheckCheck} label="Delivered" to={1212} suffix=" / 1,240" />
        <MetricCell icon={MousePointerClick} label="Clicks" to={318} />
        <MetricCell icon={Reply} label="Replies" to={87} />
        <MetricCell icon={CalendarCheck} label="Booked" to={41} />
      </div>
    </MiniCard>
  );
}

/* ------------------------------------------------------------------ */
/* Section                                                            */
/* ------------------------------------------------------------------ */

type Highlight = { visual: ReactNode; title: string; body: string };

const HIGHLIGHTS: Highlight[] = [
  {
    visual: <SegmentMock />,
    title: "The right audience, every time",
    body: "Layer simple filters to reach an exact segment, or send to your whole list in a tap. Both take seconds.",
  },
  {
    visual: <ChannelMock />,
    title: "Text, email, or both",
    body: "Prioritize text and fall back to email for anyone not opted in, or send a single channel. Everyone gets the message.",
  },
  {
    visual: <MetricsStripMock />,
    title: "Results as they happen",
    body: "Watch delivery, clicks, opens, and replies stream in live, then answer replies on the go from your inbox.",
  },
];

export function BlastsHighlights() {
  return (
    <section className="bg-background pt-20 lg:pt-28">
      <div className="mx-auto max-w-7xl px-4 lg:px-8">
        <SectionHeading
          align="left"
          size="sm"
          title={
            <>
              Sent to exactly the right people, on the channel they want.
              <span className="text-muted-foreground">
                {" "}
                Target a precise segment or your whole list, then track the results in real time.
              </span>
            </>
          }
        />
      </div>

      {/* Framed band: hatched side gutters, solid card body (Attio-style). */}
      <div className="mt-12 border-t border-border bg-grid lg:mt-16">
        <div className="mx-auto max-w-7xl px-4 lg:px-8">
          <Stagger className="grid grid-cols-1 divide-y divide-border border-x border-border bg-background lg:grid-cols-3 lg:divide-x lg:divide-y-0">
            {HIGHLIGHTS.map((h) => (
              <StaggerItem key={h.title} className="flex flex-col px-6 py-8 sm:px-8 sm:py-10">
                <div className="flex h-[188px] items-center justify-center">{h.visual}</div>
                <div className="mt-7">
                  <h3 className="text-base font-bold tracking-tight text-foreground">{h.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{h.body}</p>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </div>
    </section>
  );
}
